import type { NavigateFunction } from 'react-router';
import { ROUTES } from '@/shared/config/routes';
import type { ChatNavigationState, RootNavigationState } from '@/types/routing.types';
import type { SendMessageInput } from '@/features/chats/stores';
import { notificationService } from '@/lib/services/notification.service';
import type { FileAssetWithUrl } from '@/features/chats/model/chats.types';
import {
  checkAgentEnabled,
  getAgentMetadataHelpers,
  type AgentVisibilityScope,
} from '@/entities/agent';
import { logger } from '@/lib/utils/logger';
import { getChatStores } from '@/features/chats/lib/chat-store-bindings';
import { i18n } from '@/shared/i18n';

export interface StartChatSessionParams {
  agentId: string;
  prompt: string;
  inputs?: Record<string, unknown>;
  files?: FileAssetWithUrl[];
  knowledgeBaseIds?: string[];
  isHidden?: boolean;
  replace?: boolean;
  /**
   * Workspace + organization context for the upfront agent-visibility check.
   *
   * Required because `checkAgentEnabled` no longer reads workspace/org IDs
   * from sibling entity stores — `entities/` code MUST NOT
   * value-import from sibling entities. Callers (pages / features) are
   * responsible for resolving these locally and passing them in.
   */
  visibilityScope: AgentVisibilityScope;
}

/**
 * Utility function to start a new chat session with an agent
 * Handles chat initialization, route determination, and navigation
 *
 * @param initializeChat - Function to initialize a new chat (from useChatStoreContext)
 * @param navigate - React Router navigate function
 * @param params - Chat session parameters
 * @param params.agentId - ID of the agent to chat with
 * @param params.prompt - Initial prompt/message to send
 * @param params.inputs - Optional inputs/options for the agent
 * @param params.files - Optional file attachments
 * @param params.knowledgeBaseIds - Optional knowledge base IDs to use
 * @param params.isHidden - Whether the message should be hidden
 * @param params.replace - Whether to replace current history entry (default: false)
 * @returns Promise that resolves when chat session is started
 */
export async function startChatSession(
  initializeChat: (agentId?: string) => Promise<string>,
  navigate: NavigateFunction,
  params: StartChatSessionParams,
): Promise<void> {
  try {
    // Check if the agent is enabled/accessible before starting the chat
    const isAgentAvailable = await checkAgentEnabled(params.agentId, params.visibilityScope);

    if (!isAgentAvailable) {
      notificationService.showError(i18n.t('chats:chats.startChatSession.agentNotAvailable'));
      return;
    }

    // Check if this agent has auto-open widget behavior
    const { autoOpen, layoutMode, data } = getAgentMetadataHelpers().getInitialWidgetState(
      params.agentId,
    );

    // If the agent has auto-open widget AND there's no prompt to send,
    // navigate to NEW_CHAT_SESSION (Root) with widgetData
    if (autoOpen && data && !params.prompt) {
      // Navigate to Root with widgetData - Root will handle opening the panel synchronously
      void navigate(ROUTES.NEW_CHAT_SESSION, {
        state: {
          agentId: params.agentId,
          inputs: params.inputs,
          widgetData: {
            key: data.key,
            data: data.data,
            fullWidth: layoutMode === 'fullscreen',
          },
        } satisfies RootNavigationState,
        replace: params.replace ?? false,
      });
      return;
    }

    // For agents without auto-open widgets, clear the widget state first and then use the standard flow
    if (!autoOpen) {
      getChatStores().widgetStore.useWidgetStore.getState().clearWidgetPanel();
    }

    // Start a new chat session
    const newChatId = await initializeChat(params.agentId);

    // Prepare the message input with all provided parameters
    const messageInput: SendMessageInput = {
      agentId: params.agentId,
      prompt: params.prompt,
      inputs: params.inputs,
      files: params.files,
      knowledgeBaseIds: params.knowledgeBaseIds,
      isHidden: params.isHidden,
    };

    // Determine the target route based on stream mode
    const targetRoute = ROUTES.CHAT_SESSION(newChatId);

    // Redirect the user to the new chat session
    void navigate(targetRoute, {
      state: {
        agentId: params.agentId,
        args: messageInput,
      } satisfies ChatNavigationState,
      replace: params.replace ?? false,
    });
  } catch (error) {
    logger.error('Failed to start chat session:', error);
    notificationService.showError(i18n.t('chats:chats.startChatSession.failedToStartChat'));
    throw error;
  }
}
